import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '../lib/cn';

/**
 * Avatar + AvatarStack — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * Sizes: sm 28 / md 36 / lg 48. Tone background applies when no `src`
 * is set or while it loads (fallback initials).
 */

export type AvatarSize = 'sm' | 'md' | 'lg';
export type AvatarColor = 'primary' | 'secondary' | 'ink' | 'warning';

const SIZES: Record<AvatarSize, string> = {
  sm: 'size-7 text-xs',
  md: 'size-9 text-sm',
  lg: 'size-12 text-base',
};

const COLORS: Record<AvatarColor, string> = {
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  // text-background, not text-white: white on ink is 2.18:1 once
  // ink resolves to its dark-theme value. Measured 11.01 / 8.46.
  ink: 'bg-ink text-background',
  // bg-warning + white was 3.39:1 in BOTH themes — a pre-existing AA failure,
  // not a dark-theme one. warning-dark + background is 4.83 / 8.95.
  warning: 'bg-warning-dark text-background',
};

interface AvatarProps {
  src?: string;
  alt?: string;
  initials?: string;
  size?: AvatarSize;
  color?: AvatarColor;
  className?: string;
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, alt, initials, size = 'md', color = 'primary', className }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold leading-none',
        SIZES[size],
        COLORS[color],
        className
      )}
    >
      {src && (
        <AvatarPrimitive.Image src={src} alt={alt ?? ''} className="aspect-square h-full w-full" />
      )}
      <AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center">
        {initials ?? '?'}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
);
Avatar.displayName = 'Avatar';

interface AvatarStackProps {
  max?: number;
  children: React.ReactNode;
  className?: string;
}

const AvatarStack = ({ max = 3, children, className }: AvatarStackProps) => {
  const items = React.Children.toArray(children);
  const shown = items.slice(0, max);
  const overflow = items.length - shown.length;

  return (
    <div className={cn('flex items-center -space-x-2', className)}>
      {shown.map((child, i) => (
        <span key={i} className="ring-2 ring-card rounded-full">
          {child}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex size-9 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground ring-2 ring-card">
          +{overflow}
        </span>
      )}
    </div>
  );
};

export { Avatar, AvatarStack };
export type { AvatarProps, AvatarStackProps };
